import React, { useCallback, useEffect, useMemo, useState } from "react";
import ModalBasic from "./ModalBasic.jsx";
import {useDispatch, useSelector} from "react-redux";
import { PrimaryButton, SecondaryButton } from "./Buttons.jsx";
import { agreeToTerms, emailTerms } from "../api/settings.js";
import {getMerchants} from "../store/app/action.js";

const TosModal = () => {
  const { selectedMerchant, user } = useSelector((state) => state.app);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [showEmailMeModal, setShowEmailMeModal] = useState(false);
  const [email, setEmail] = useState("");
  const [agreeing, setAgreeing] = useState(false);
  const dispatch = useDispatch();

  const showModal = useMemo(() => {
    if (!selectedMerchant || !user) return false;
    if (user.type === "saucey_admin") {
      return false;
    }
    if(!selectedMerchant.direct) {
      return false;
    }
    setEmail(user.email);
    return !selectedMerchant.tos_agreed;
  }, [selectedMerchant, user]);

  const [checkboxes, setCheckboxes] = useState({
    termsOfUse: false,
    acceptableUse: false,
    privacyPolicy: false,
  });

  const areAllChecked = useMemo(() => {
    return Object.values(checkboxes).every((checked) => checked);
  }, [checkboxes]);

  const handleCheckboxChange = useCallback((e) => {
    const { name, checked } = e.target;
    setCheckboxes((prevState) => ({
      ...prevState,
      [name]: checked,
    }));
  }, []);

  return (
    <>
      <ModalBasic
        width={"xl"}
        modalOpen={showModal}
        setModalOpen={() => {}}
        hideCloseButton
        title={"Terms of Service Agreement"}
      >
        <div className="text-sm">
          <p>
            Welcome to Saucey Direct. We are excited to have you as a partner.
            Before you can continue, you must review and agree to the following
            agreements and policies.{" "}
          </p>
          <p className={"pt-4 pb-2"}>
            Please check the boxes below to indicate that you have read and
            agree to the following:
          </p>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              if (!areAllChecked) {
                return;
              }
              setAgreeing(true)
              try {
                await agreeToTerms();
              } catch(e) {
                setAgreeing(false)
              } finally {
                dispatch(getMerchants());
              }

            }}
          >
            <ul className="list-disc list-inside">
              <div className="m-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="termsOfUse"
                    className="form-checkbox"
                    checked={checkboxes.termsOfUse}
                    onChange={handleCheckboxChange}
                  />
                  <span className="text-sm ml-2 mr-2">Terms of Use</span>
                  <SecondaryButton
                    size={"xs"}
                    onClick={(e) => {
                      e.preventDefault();
                      window.open(
                        "https://www.sauceydirect.com/terms-of-use",
                        "_blank"
                      );
                    }}
                  >
                    View
                  </SecondaryButton>
                </label>
              </div>
              <div className="m-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="acceptableUse"
                    className="form-checkbox"
                    checked={checkboxes.acceptableUse}
                    onChange={handleCheckboxChange}
                  />
                  <span className="text-sm ml-2 mr-2">Acceptable Use</span>
                  <SecondaryButton
                    size={"xs"}
                    onClick={(e) => {
                      e.preventDefault();
                      window.open(
                        "https://www.sauceydirect.com/acceptable-use",
                        "_blank"
                      );
                    }}
                  >
                    View
                  </SecondaryButton>
                </label>
              </div>
              <div className="m-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    name="privacyPolicy"
                    className="form-checkbox"
                    checked={checkboxes.privacyPolicy}
                    onChange={handleCheckboxChange}
                  />
                  <span className="text-sm ml-2 mr-2">Privacy Policy</span>
                  <SecondaryButton
                    size={"xs"}
                    onClick={(e) => {
                      e.preventDefault();
                      window.open(
                        "https://www.sauceydirect.com/privacy-policy",
                        "_blank"
                      );
                    }}
                  >
                    View
                  </SecondaryButton>
                </label>
              </div>
            </ul>
            <div className={"flex gap-2 justify-end"}>
              <SecondaryButton
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setShowEmailMeModal(true);
                }}
              >
                Email Me a Copy
              </SecondaryButton>

              <PrimaryButton type="submit" disabled={!areAllChecked} loading={agreeing}>
                Agree
              </PrimaryButton>
            </div>
          </form>
        </div>
      </ModalBasic>

      <ModalBasic
        width={"md"}
        modalOpen={showEmailMeModal}
        setModalOpen={setShowEmailMeModal}
        title={"Email Me a Copy"}
      >
        <div className="text-sm">
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              setSending(true);
              try {
                await emailTerms(email);
              } catch (error) {
              } finally {
                setSending(false);
                setSent(true);
                setTimeout(() => {
                  setShowEmailMeModal(false);
                  setSent(false);
                  setEmail(user.email);
                }, 1500);
              }
            }}
          >
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
            </div>
            <div className={"flex gap-2 justify-end"}>
              <SecondaryButton
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  setShowEmailMeModal(false);
                }}
              >
                Cancel
              </SecondaryButton>
              <PrimaryButton type="submit" loading={sending || sent} success={sent}>Send</PrimaryButton>
            </div>
          </form>
        </div>
      </ModalBasic>
    </>
  );
};

export default TosModal;
